/* npm modules outside of vrbo */
import React, {Fragment} from 'react';
import {connect} from 'react-redux';
import {compose, graphql} from 'react-apollo';
import {withRouter} from 'react-router-dom';
import PropTypes from 'prop-types';
import {get, isEmpty} from 'lodash';

/* Atoms (custom) */
import {Typography} from '../../atoms/Typography/Typography';

/* Molecules (baseline) */
import {
    edap,
    toasterActions,
    Waypoint,
} from '../../molecules/baseline/baseline';

/* Molecules (custom) */
import {AddGuestsModal} from '../../molecules/AddGuestsModal/AddGuestsModal';
import {HasGuests} from '../../molecules/HasGuests/HasGuests';
import {HasNoGuests} from '../../molecules/HasNoGuests/HasNoGuests';

/* Queries and mutations */
import {AddGuests, DeleteGuests} from './GuestsOfGuestMutations';
import {GuestsOfGuestsQuery} from './GuestsOfGuestsQuery';

/* EDAP events */
import {
    addGuestEmailInputted,
    addGuestPresented,
    addGuestSelected,
    addGuestSubmitted,
    addGuestSucceeded,
    manageGuestsSelected,
    removeGuestSelected,
} from '../../../utils/EDAP/Conversation';

/* Utils */
import {
    getActionLocation,
    updateApolloCacheForGuests,
} from './utils/GuestsOfGuest.util';
import {guestsAlteredToast} from './utils/guestsAlteredToast.util';
import translate from '../../../utils/translations';
import isValidEmail from '../../../utils/emailMixin';
import {isExperimentBucketed} from '../../../utils/experimentBucket';

/* Constants*/
import {
    GOG_COPY_UPDATE_EXPERIMENT,
    getTranslatedLabels,
    REMOVE_GUEST,
    SEND_INVITE,
} from './constants';

/* Styles */
import './GuestsOfGuest.less';

export class GuestsOfGuest extends React.Component {
    static propTypes = {
        actionLocation: PropTypes.string.isRequired,
        addGuestUrl: PropTypes.string,
        addToast: PropTypes.func,
        conversationId: PropTypes.string.isRequired,
        reservationUuid: PropTypes.string,
        translations: PropTypes.shape({}),
    };

    static defaultProps = {
        addToast: () => {},
        translations: {},
    };

    constructor(props, context) {
        super(props, context);
        this.state = {
            disableSend: true,
            errorMsg: '',
            existingRecipients: [],
            invalidRecipients: 0,
            isLoading: false,
            isOpen: this.props.isOpen,
            recipients: [],
            removingGuestEmail: '',
        };

        const {actionLocation, history} = this.props;
        this.isPrimary =
            this.props.data?.getTravelerStay?.booking?.isPrimaryGuest ===
                true || false;
        this.actionLocation = getActionLocation(actionLocation, history);
        this.fromGuestsRoute = false;
    }

    componentDidMount() {
        const routeState = this.props.history.location.state;
        if (routeState?.guests?.length > 0) {
            this.fromGuestsRoute = true;
        }
    }

    handleInviteButtonClick = () => {
        const {booking, stayGuestOfGuests: guests} =
            this.props.data?.getTravelerStay || {};
        const {
            guests: guestsFromBooking,
            periodOfStay,
            uuid: reservationUUID,
        } = booking || {};
        const {adults, children} = guestsFromBooking || {};
        const {
            checkInDate: reservationArrival,
            checkOutDate: reservationDeparture,
        } = periodOfStay || {};

        const actionLocation = this.actionLocation;
        this.setState({isOpen: true});

        if (guests && guests.length > 1) {
            edap().push({
                name: 'generic.event',
                data: {
                    eventcategory: 'stay',
                    eventaction: 'details.manageguests.click',
                    eventlabel: guests.length,
                },
            });
            manageGuestsSelected({
                actionLocation,
                reservationUUID,
                reservationArrival,
                reservationDeparture,
                adults,
                children,
            });
        } else {
            edap().push({
                name: 'invite.initiated',
                data: {
                    accesstype: this.isPrimary ? 'owner' : 'collaborator',
                    entitytype: 'stay',
                    entityvalue: reservationUUID,
                },
            });
            addGuestSelected({
                actionLocation,
                reservationUUID,
                reservationArrival,
                reservationDeparture,
                adults,
                children,
            });
        }
    };
    removeGuestsPath = (guests) => {
        const {history} = this.props;
        const pathName = history.location.pathname || '';
        const guestsIndex = pathName.indexOf('/guests');
        if (guestsIndex !== -1) {
            history.push({
                pathname: pathName.slice(0, guestsIndex),
                state: {
                    guests: guests || [],
                },
            });
        }
    };

    handleClose = () => {
        this.setState({isOpen: false});
        this.removeGuestsPath();
        edap().push({
            name: 'generic.modal',
            data: {
                modalaction: 'close',
                modalname: 'GuestsOfGuest',
            },
        });
    };

    onSubmit = (event) => {
        let recipients = [];
        event.preventDefault();

        if (this.state.recipients.length > 0 && !this.state.disableSend) {
            this.state.recipients.forEach((recipient) => {
                if (isValidEmail(recipient)) {
                    recipients.push({
                        email: recipient,
                    });
                }
            });
            this.sendInvite(recipients);
        }
    };

    sendInvite = (reservationGuests, resend = false) => {
        const {conversationId} = this.props;
        const {booking} = this.props.data?.getTravelerStay || {};
        const {
            guests: guestsFromBooking,
            periodOfStay,
            uuid: reservationUUID,
        } = booking || {};
        const {adults, children} = guestsFromBooking || {};
        const {
            checkInDate: reservationArrival,
            checkOutDate: reservationDeparture,
        } = periodOfStay || {};
        const actionLocation = this.actionLocation;
        addGuestSubmitted({
            actionLocation,
            reservationUUID,
            reservationArrival,
            reservationDeparture,
            adults,
            children,
        });

        this.setState({
            errorMsg: '',
            isLoading: true,
        });
        this.props
            .addGuests({
                variables: {
                    request: {
                        conversationUUID: conversationId,
                        reservationGuests,
                    },
                },
                update: (store, mutationResponse) => {
                    const addGuestsResponse = mutationResponse?.data?.addGuests;
                    updateApolloCacheForGuests(
                        store,
                        addGuestsResponse,
                        conversationId,
                        GuestsOfGuestsQuery
                    );
                },
            })
            .then((res) => {
                addGuestSucceeded({
                    actionLocation,
                    reservationUUID,
                    reservationArrival,
                    reservationDeparture,
                    adults,
                    children,
                });
                this.onRequestSentDone(res, reservationGuests.length, resend);
            })
            .catch(() => {
                this.onRequestError();
            });
    };

    onRequestSentDone = (res, numberInvited = 1, resend = false) => {
        const guests = get(res, 'data.addGuests', []);
        const {addToast, translations} = this.props;

        if (!guests || isEmpty(guests)) {
            this.onRequestError();
        } else {
            this.setState({
                isLoading: false,
                isOpen: false,
                recipients: [],
            });
            if (this.fromGuestsRoute) {
                this.fromGuestsRoute = false;
                this.props.history.replace({
                    pathname: this.props.location.pathname,
                    state: null,
                });
            }
            guestsAlteredToast({
                action: SEND_INVITE,
                addToast,
                numberInvited,
                resend,
                translations,
            });
        }
        this.removeGuestsPath(guests);
    };

    onRequestError = () => {
        this.setState({
            errorMsg: translate(
                this.props.translations,
                'trav-dash/trav-home',
                'messages',
                'th.inbox.conversation.reply.error'
            ),
            isLoading: false,
        });
    };

    handleRecipientsChange = (recipients, invalidTokens = []) => {
        const {booking} = this.props.data?.getTravelerStay || {};
        const {
            guests: guestsFromBooking,
            periodOfStay,
            uuid: reservationUUID,
        } = booking || {};
        const {adults, children} = guestsFromBooking || {};
        const {
            checkInDate: reservationArrival,
            checkOutDate: reservationDeparture,
        } = periodOfStay || {};
        const actionLocation = this.actionLocation;

        if (invalidTokens.length > 0 || recipients.length === 0) {
            this.setState({disableSend: true});
        } else {
            this.setState({disableSend: false});
        }

        if (recipients.length > this.state.existingRecipients.length) {
            addGuestEmailInputted({
                actionLocation,
                adults,
                children,
                reservationArrival,
                reservationDeparture,
                reservationUUID,
            });
        }

        this.setState({
            existingRecipients: recipients,
            recipients,
            invalidRecipients: invalidTokens.length,
        });
    };

    removeGuest = (reservationGuest) => {
        const {conversationId, translations} = this.props;
        const {booking} = this.props.data?.getTravelerStay || {};
        const {
            guests: guestsFromBooking,
            periodOfStay,
            uuid: reservationUUID,
        } = booking || {};
        const {adults, children} = guestsFromBooking || {};
        const {
            checkInDate: reservationArrival,
            checkOutDate: reservationDeparture,
        } = periodOfStay || {};
        const actionLocation = this.actionLocation;

        removeGuestSelected({
            actionLocation,
            reservationUUID,
            reservationArrival,
            reservationDeparture,
            adults,
            children,
        });

        this.setState({
            errorMsg: '',
            removingGuestEmail: reservationGuest.email,
        });

        this.props
            .deleteGuests({
                variables: {
                    request: {
                        conversationUUID: conversationId,
                        reservationGuests: [reservationGuest],
                    },
                },
                update: (store, mutationResponse) => {
                    const deleteGuestsResponse =
                        mutationResponse?.data?.deleteGuests;
                    updateApolloCacheForGuests(
                        store,
                        deleteGuestsResponse,
                        conversationId,
                        GuestsOfGuestsQuery
                    );
                },
            })
            .then((res) => {
                const guests = get(res, 'data.deleteGuests', []);
                const {addToast} = this.props;
                if (!guests || isEmpty(guests)) {
                    this.onRequestError();
                }

                this.setState(
                    {
                        removingGuestEmail: '',
                    },
                    () => {
                        if (this.fromGuestsRoute) {
                            this.fromGuestsRoute = false;
                            this.props.history.replace({
                                pathname: this.props.location.pathname,
                                state: null,
                            });
                        }
                    }
                );
                guestsAlteredToast({
                    action: REMOVE_GUEST,
                    addToast,
                    translations,
                });
            })
            .catch(() => {
                this.onRequestError();
            });
    };

    onEnter = () => {
        const {booking} = this.props.data?.getTravelerStay || {};
        const {periodOfStay, uuid: reservationUUID} = booking || {};
        const {
            checkInDate: reservationArrival,
            checkOutDate: reservationDeparture,
        } = periodOfStay || {};
        const actionLocation = this.actionLocation;

        addGuestPresented({
            actionLocation,
            reservationUUID,
            reservationDeparture,
            reservationArrival,
        });
    };

    tooltipContent = ({
        ACCESS_TOOLTIP_BULLET1,
        ACCESS_TOOLTIP_BULLET2,
        ACCESS_TOOLTIP_BULLET3,
        ACCESS_TOOLTIP_TEXT_1,
        ACCESS_TOOLTIP_TEXT_2,
        ACCESS_TOOLTIP_TEXT_3,
    }) => {
        return isExperimentBucketed(GOG_COPY_UPDATE_EXPERIMENT) ? (
            <Fragment>
                <Typography content={ACCESS_TOOLTIP_TEXT_1} TagName="p" />
                <Typography content={ACCESS_TOOLTIP_TEXT_2} TagName="p" />
            </Fragment>
        ) : (
            <Fragment>
                <Typography content={ACCESS_TOOLTIP_TEXT_3} TagName="p" />
                <Typography
                    className="GuestViewAccess__bullet"
                    content={ACCESS_TOOLTIP_BULLET1}
                    TagName="li"
                />
                <Typography
                    className="GuestViewAccess__bullet"
                    content={ACCESS_TOOLTIP_BULLET2}
                    TagName="li"
                />
                <Typography
                    className="GuestViewAccess__bullet"
                    content={ACCESS_TOOLTIP_BULLET3}
                    TagName="li"
                />
            </Fragment>
        );
    };

    render() {
        const {
            addGuestUrl,
            data,
            reservationUuid: reservationUUID,
            translations,
        } = this.props;
        let {stayGuestOfGuests} = data?.getTravelerStay || {};

        if (!reservationUUID) {
            return null;
        }

        let guestCount = '';

        const {invalidRecipients, recipients} = this.state;

        if (recipients.length - invalidRecipients) {
            guestCount = `(${recipients.length - invalidRecipients})`;
        }

        const {
            ACCESS_TOOLTIP_BULLET1,
            ACCESS_TOOLTIP_BULLET2,
            ACCESS_TOOLTIP_BULLET3,
            ACCESS_TOOLTIP_TEXT_1,
            ACCESS_TOOLTIP_TEXT_2,
            ACCESS_TOOLTIP_TEXT_3,
            ACCESS_WHAT_CAN_GUEST_SEE_TEXT,
            ACCESS_WHAT_CAN_THEY_SEE_TEXT,
            ADD_CREW_HEADING,
            ADD_CREW_POST_INVITE_HEADING,
            ADD_GUEST_MODAL_SUB_HEADING,
            ADD_GUEST_SUB_HEADING,
            ADD_GUESTS_LABEL,
            ADD_GUESTS_TRIP_LABEL,
            EMAIL_REMOVE_LABEL,
            EMAIL_TEXTBOX_LABEL,
            GUEST_VIEW_DETAILS,
            GUESTS_LABEL,
            INVITE_CREW_LABEL,
            INVITE_PEOPLE_LABEL,
            INVITE_SENT_LABEL,
            PEOPLE_LABEL,
            SEND_INVITE_LABEL_COUNT,
            WHOS_GOING_HEADING,
        } = getTranslatedLabels({guestCount, translations});

        const ADD_GUESTS_LABEL_COUNT = `${ADD_GUESTS_LABEL} ${guestCount}`;
        const isGogCopyUpdateExperimentBucketed = isExperimentBucketed(
            GOG_COPY_UPDATE_EXPERIMENT
        );

        const routeState = this.props.history.location.state;
        if (this.fromGuestsRoute) {
            stayGuestOfGuests = routeState?.guests;
        }

        const hasGuests = stayGuestOfGuests?.length > 1;

        return (
            <Waypoint
                onEnter={this.onEnter}
                className="GuestsOfGuest__rightRailReorder"
            >
                <div className="GuestsOfGuest rounded-border hidden-print">
                    {hasGuests ? (
                        <HasGuests
                            actionLocation={this.actionLocation}
                            addGuestsModalButtonLabel={
                                isGogCopyUpdateExperimentBucketed
                                    ? INVITE_SENT_LABEL
                                    : ADD_GUESTS_LABEL
                            }
                            addGuestUrl={addGuestUrl}
                            addGuestsWidgetHeading={
                                isGogCopyUpdateExperimentBucketed
                                    ? ADD_CREW_POST_INVITE_HEADING
                                    : WHOS_GOING_HEADING
                            }
                            guestAccessLabel={
                                isGogCopyUpdateExperimentBucketed
                                    ? ACCESS_WHAT_CAN_THEY_SEE_TEXT
                                    : ACCESS_WHAT_CAN_GUEST_SEE_TEXT
                            }
                            guests={stayGuestOfGuests}
                            guestsLabel={
                                isGogCopyUpdateExperimentBucketed
                                    ? PEOPLE_LABEL
                                    : GUESTS_LABEL
                            }
                            handleInviteButtonClick={
                                this.handleInviteButtonClick
                            }
                            removeGuest={this.removeGuest}
                            removingGuestEmail={this.state.removingGuestEmail}
                            sendInvite={this.sendInvite}
                            tooltipContent={this.tooltipContent({
                                ACCESS_TOOLTIP_BULLET1,
                                ACCESS_TOOLTIP_BULLET2,
                                ACCESS_TOOLTIP_BULLET3,
                                ACCESS_TOOLTIP_TEXT_1,
                                ACCESS_TOOLTIP_TEXT_2,
                                ACCESS_TOOLTIP_TEXT_3,
                            })}
                            translations={translations}
                        />
                    ) : (
                        <HasNoGuests
                            actionLocation={this.actionLocation}
                            addGuestUrl={addGuestUrl}
                            addGuestsWidgetButtonLabel={
                                isGogCopyUpdateExperimentBucketed
                                    ? INVITE_CREW_LABEL
                                    : ADD_GUESTS_LABEL
                            }
                            guestsDetailsNoGuestsWrapper={
                                isGogCopyUpdateExperimentBucketed ? (
                                    <Fragment>
                                        <h3>{ADD_CREW_HEADING}</h3>
                                        <p>{ADD_GUEST_SUB_HEADING}</p>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <h3>{ADD_GUESTS_TRIP_LABEL}</h3>
                                        <p>{GUEST_VIEW_DETAILS}</p>
                                    </Fragment>
                                )
                            }
                            handleInviteButtonClick={
                                this.handleInviteButtonClick
                            }
                        />
                    )}
                    <AddGuestsModal
                        buttonLabel={
                            isGogCopyUpdateExperimentBucketed
                                ? SEND_INVITE_LABEL_COUNT
                                : ADD_GUESTS_LABEL_COUNT
                        }
                        handleClose={this.handleClose}
                        handleRecipientsChange={this.handleRecipientsChange}
                        isValidEmail={isValidEmail}
                        label={EMAIL_TEXTBOX_LABEL}
                        modalTitle={
                            isGogCopyUpdateExperimentBucketed
                                ? INVITE_PEOPLE_LABEL
                                : ADD_GUESTS_TRIP_LABEL
                        }
                        modalSubTitle={
                            isGogCopyUpdateExperimentBucketed
                                ? ADD_GUEST_MODAL_SUB_HEADING
                                : ''
                        }
                        onSubmit={this.onSubmit}
                        removeLabel={EMAIL_REMOVE_LABEL}
                        state={this.state}
                    />
                </div>
            </Waypoint>
        );
    }
}

const mapStateToProps = (state) => ({
    ...state,
});

const mapDispatchToProps = {
    addToast: toasterActions.addToast,
};

export default compose(
    graphql(GuestsOfGuestsQuery, {
        skip: ({reservationUuid}) => !reservationUuid,
        options: (ownProps) => ({
            variables: {
                conversationId: ownProps.conversationId,
            },
            errorPolicy: 'all',
        }),
    }),
    graphql(AddGuests, {name: 'addGuests'}),
    graphql(DeleteGuests, {name: 'deleteGuests'}),
    connect(mapStateToProps, mapDispatchToProps)
)(withRouter(GuestsOfGuest));
